import { Component, Vue, Prop } from 'vue-property-decorator'
import AppDatatable from '@core/components/AppDatatable.vue'
import AppForm from '@core/components/AppForm.vue'
import { FormStructure } from '@master/model/structure/FormStructure'
import PayeeMeta from '../model/PayeeMeta'
import API from '@core/services/API'
import { inject } from 'inversify-props'
import { isNull, startCase, pick, sortBy } from 'lodash'
import { IDateService } from '@master/services/IDateService'
import { AssignedPolicyDTO, UnAssignedPolicyDTO, CommissionIntermediariesService } from '../api'
import CSVImport from '@core/services/csv/CSVImport.vue'
import AccountMeta from '@core/pages/account/model/AccountMeta'
import AppDialog from '@core/components/AppDialog.vue'
import { IProjectInfo } from '@master/config/IProjectInfo'
import { SharedVariable } from '@core/model/SharedVariable'
import { UIService } from '@core/services/UIService'
import { IntermediariesMeta, IntermediariesRole } from '../model/IntermediariesMeta'
import { downloadCSVFile } from '@core/services/CSVService'
import { CountryOptions } from '@master/model/options/NationalityAndCountryOptions'

@Component({
  components: {
    AppDatatable,
    AppForm,
    CSVImport,
    AppDialog
  }
})
export class IntermediaryMixin extends Vue {
    @Prop({ default: undefined }) uiIntermediaryRole!: IntermediariesRole

    @inject()
    protected dateService: IDateService

    @inject()
    protected $api: API

    @inject()
    protected projectInfo: IProjectInfo

    @inject()
    protected sharedVariable: SharedVariable

    protected uiService: UIService
    // protected uiIntermediaryRole: IntermediariesRole = undefined
    protected isSaving = false
    protected massImport = false
    protected loading = false
    protected showNewIntermediaryModal = false
    protected txtSearchTop = ''
    protected newIntermediaryData = new PayeeMeta()
    protected ifamList: AccountMeta[] = []
    protected payeeList: PayeeMeta[] = []
    protected unAssignedPolicies: UnAssignedPolicyDTO[] = []
    protected policyList: AssignedPolicyDTO[] = []
    protected newIntermediary: IntermediariesMeta = null
    protected forUpdate = false

    intermediaryData: IntermediariesMeta[] = []

    get companyName () {
      return `${this.projectInfo.companyShortName} Code` ?? 'Company Code'
    }

    get pageTitle () {
      return startCase(this.intermediaryRole)
    }

    get newIntermediaryDataDetails (): FormStructure<PayeeMeta> {
      return [
        {
          key: 'payeeFullname',
          label: 'Full Name',
          classes: ['colspan']
        },
        {
          key: 'payeeNric',
          label: 'NRIC/FIN/Passport/UEN No.'
        },
        {
          label: this.companyName,
          key: 'payeeCompanyCode'
        },
        {
          key: 'payeeContact',
          label: 'Contact'
        },
        {
          key: 'payeeEmail',
          label: 'Email'
        },
        {
          type: 'date',
          key: 'payeeDob',
          label: 'Date of Birth/ Incorporation'
        },
        {
          key: 'payeePob',
          label: 'Place of Birth/ Incorporation'
        },
        {
          key: 'payeeAddress',
          label: 'Residential/ Registered Address',
          classes: ['colspan']
        },
        {
          key: 'payeePostalCode',
          label: 'Postal Code'
        },
        {
          label: 'Country',
          key: 'payeeCountry',
          options: CountryOptions
        },
        {
          key: 'payeeBankName',
          label: 'Bank Name'
        },
        {
          key: 'payeeBank',
          label: 'Bank Account No'
        },
        {
          key: 'payeeSwiftCode',
          label: 'SWIFT Code'
        }
      ]
    }

    get intermediaryRole () {
      return this.uiIntermediaryRole
    }

    get modalTitle () {
      return this.forUpdate ? `Update ${startCase(this.intermediaryRole)}` : `Add New ${startCase(this.intermediaryRole)}`
    }

    get userList (): Array<any> {
      let list = []
      if (this.newIntermediary.isIfamUser === true) {
        if (!isNull(this.ifamList)) {
          list = this.ifamList.map(user => {
            return {
              label: user.fullname || user.names[0],
              value: user.oid
            }
          })
        }
      } else if (this.newIntermediary.isIfamUser === false) {
        if (!isNull(this.payeeList)) {
          list = this.payeeList.map(user => {
            return {
              label: user.payeeFullname,
              value: user.oid
            }
          })
        }
      }
      return sortBy(list, (user) => user.label)
    }

    get listOfPolicy () {
      let list = []
      if (this.forUpdate) {
        list = this.policyList.map(e => {
          return {
            label: e.policyNumber,
            value: e.policyNumber
          }
        })
      } else {
        list = this.unAssignedPolicies.map(e => {
          return {
            label: e.policyNumber,
            value: e.policyNumber
          }
        })
      }
      return sortBy(list, (policy) => policy.label)
    }

    async fetchPolicyList () {
      const { assignedPolicies, unAssignedPolicies } = await CommissionIntermediariesService.fetchPolicyList(this.intermediaryRole)
      this.policyList = assignedPolicies
      this.unAssignedPolicies = unAssignedPolicies
    }

    async fetchIfamUserList () {
      const headers = [
        '_oid',
        'fullname',
        'names',
        'id_number',
        'idNumber',
        'mas_repno',
        'email'
      ]

      const res = await this.$api.account.list({
        headers: JSON.stringify(headers)
      })
      this.ifamList = res.data.map(_user => {
        const user = new AccountMeta()
        user.assignData(headers, _user)
        return user
      })
    }

    async fetchPayeeList () {
      const headers = [
        '_oid',
        'payee_fullname',
        'payee_nric',
        'payee_email'
      ]

      const res = await this.$api.intermediaries.list({
        headers: JSON.stringify(headers)
      })
      this.payeeList = res.data.map(_payee => {
        const payee = new PayeeMeta()
        payee.assignData(headers, _payee)
        return payee
      })
    }

    async fetchIntermediaryList () {
      const intermediariesTableData = await CommissionIntermediariesService.fetchIntermediaryList(this.intermediaryRole)

      this.intermediaryData = intermediariesTableData.map(data => {
        return {
          ...data,
          emit: true
        }
      })
    }

    getIntermediaryFromPicodedSM (userOid) {
      const ifam = this.ifamList.find(user => user.oid === userOid)

      if (ifam) {
        return {
          nric: ifam?.idNumber,
          fullName: ifam?.fullname,
          email: ifam?.email
        }
      }
      return {
        nric: '',
        fullName: '',
        email: ''
      }
    }

    getIntermediaryFromPayee (userOid) {
      const payee = this.payeeList.find(payee => payee.oid === userOid)
      if (payee) {
        return {
          nric: payee?.payeeNric || '',
          fullName: payee?.payeeFullname || '',
          email: payee?.payeeEmail || ''
        }
      }
      return {
        nric: '',
        fullName: '',
        email: ''
      }
    }

    atModalClose () {
      this.reset()
    }

    rowClicked (row: IntermediariesMeta) {
      this.newIntermediary = pick(row, Object.keys(new IntermediariesMeta(this.intermediaryRole))) as IntermediariesMeta

      if (this.newIntermediary.createNewPayee) {
        this.newIntermediary.createNewPayee = false
      }
      this.forUpdate = true
      this.showNewIntermediaryModal = true
    }

    addRecordOnClick () {
      this.newIntermediary = new IntermediariesMeta(this.intermediaryRole)
      this.showNewIntermediaryModal = true
    }

    newIntermediaryIsInvalid (): boolean {
      const checkKeys = Object.keys(new IntermediariesMeta(this.intermediaryRole))

      return checkKeys.some(key => {
        if (this.newIntermediary.createNewPayee && key === 'userSystemOid') return false
        if (key === 'oid') return false
        if (key === 'nric' || key === 'name' || key === 'email') return false
        if (this.intermediaryRole === 'introducer' && key === 'commissionPct') return false
        if (this.intermediaryRole !== 'introducer' && (key === 'year1Commission' || key === 'renewalCommission')) return false
        return this.newIntermediary[key] === null || this.newIntermediary[key] === undefined || this.newIntermediary[key] === ''
      })
    }

    checkCommissionPercentageIsValid () {
      if (this.newIntermediary.role !== 'introducer') {
        const percentage = this.newIntermediary?.commissionPct || 0
        if (percentage > 100) {
          return false
        }
      }
      return true
    }

    newIntermediaryDataIsInvalid (): boolean {
      return !(Object.values(this.newIntermediaryData).some(val => {
        return !(val === null || val === undefined || val === '')
      }))
    }

    async addNewIntermediary () {
      this.isSaving = true
      if (this.newIntermediaryIsInvalid()) {
        this.isSaving = false
        // eslint-disable-next-line no-throw-literal
        throw 'Please fill in all input'
      }

      if (!this.checkCommissionPercentageIsValid()) {
        await this.uiService.alert.show({
          type: 'error',
          message: 'Percentage cannot more than 100%'
        })
        this.isSaving = false
        return
      }

      // save data to payee table if it is selected to new Intermediary
      if (this.newIntermediary.createNewPayee) {
        if (this.newIntermediaryDataIsInvalid()) {
          this.isSaving = false
          // eslint-disable-next-line no-throw-literal
          throw 'Please fill in some detail for new intermediary'
        }

        const payee = await this.$api.intermediaries.meta({
          meta: {
            ...this.newIntermediaryData.convertToRawData(),
            // eslint-disable-next-line @typescript-eslint/camelcase
            payee_dob: this.dateService.format(Number(this.newIntermediaryData.payeeDob), 'YYYY-MM-DD')
          },
          _oid: 'new'
        })
        const { updateMeta } = payee
        this.newIntermediary.userSystemOid = updateMeta._oid
        this.newIntermediary.isIfamUser = false
        this.newIntermediary.email = updateMeta.payee_email
        this.newIntermediary.nric = updateMeta.payee_nric
        this.newIntermediary.name = updateMeta.payee_fullname
        await this.fetchPayeeList()
      }

      // save the intermediary to rel intermediaries table
      try {
        const id = this.uiService.randomID()
        this.uiService.loading.addLoadingTask(id)
        if (this.forUpdate) {
          await CommissionIntermediariesService.updateIntermediary(this.newIntermediary.oid, this.newIntermediary)
        } else {
          await CommissionIntermediariesService.createNewIntermediaries(this.intermediaryRole, [this.newIntermediary])
        }

        this.reset()

        this.uiService = this.sharedVariable.uiService

        await this.fetchPolicyList()
        await this.fetchPayeeList()
        await this.fetchIntermediaryList()
        this.uiService.loading.removeLoadingTask(id)
      } catch (error) {
        await this.uiService.alert.show({
          type: 'error',
          message: error
        })
        this.isSaving = false
      }
    }

    reset () {
      this.showNewIntermediaryModal = false
      this.isSaving = false
      this.forUpdate = false
      this.massImport = false
      this.newIntermediary = new IntermediariesMeta(this.intermediaryRole)
      this.newIntermediaryData = new PayeeMeta()
    }

    async importCsv (records) {
      this.uiService = this.sharedVariable.uiService
      const id = this.uiService.randomID()
      this.uiService.loading.addLoadingTask(id)

      try {
        this.isSaving = true
        const importedList = records.map((row) => {
          const { userSystemOid, nric, isIfamUser } = row
          if (!userSystemOid) {
            const errorMessage = isIfamUser
              ? `NRIC/FIN/Passport/UEN No. '${nric}' does not exist in IFAM users list`
              : `NRIC/FIN/Passport/UEN No. '${nric}' does not exist in Non-IFAM users list`
            throw Error(errorMessage)
          }

          return {
            policyNumber: row.policyNumber,
            nric: row.nric,
            name: row?.name,
            email: row?.email,
            userSystemOid: row.userSystemOid,
            year1Commission: row?.year1Commission,
            renewalCommission: row?.renewalCommission,
            commissionPct: Number(row?.commissionPct || 0),
            isLinkNBR: false,
            isIfamUser: row.isIfamUser,
            role: this.intermediaryRole,
            createNewPayee: false
          }
        })

        await CommissionIntermediariesService.createNewIntermediaries(this.intermediaryRole, importedList)
        this.reset()

        await this.fetchPolicyList()
        await this.fetchPayeeList()
        await this.fetchIntermediaryList()
      } catch (error) {
        await this.uiService.alert.show({
          type: 'error',
          message: error
        })
        this.isSaving = false
      } finally {
        this.isSaving = false
      }
      this.uiService.loading.removeLoadingTask(id)
    }

    get exportCSVObject () {
      return {
        csvData: null,
        header: null,
        fileName: ''
      }
    }

    async exportCsv () {
      downloadCSVFile(this.exportCSVObject.header, this.exportCSVObject.csvData, this.exportCSVObject.fileName)
    }

    async mounted () {
      this.uiService = this.sharedVariable.uiService

      const id = this.uiService.randomID()
      this.uiService.loading.addLoadingTask(id)
      try {
        await this.fetchIfamUserList()
        await this.fetchPolicyList()
        await this.fetchPayeeList()
        await this.fetchIntermediaryList()
      } finally {
        this.uiService.loading.removeLoadingTask(id)
      }
    }
}
